<template>
  <!--
    Reka teleports this <li> into the ZoneToastStack viewport; its place in the template only
    decides provider context, never position on screen.
  -->
  <ToastRoot
    :open="isOpen"
    :type="sensitivity"
    :duration="duration"
    :class="toastClasses"
    data-testid="toast"
    @update:open="onOpenChange"
    @escape-key-down="onEscapeKeyDown"
    @pause="$emit('pause')"
    @resume="$emit('resume')"
  >
    <div class="flex w-full flex-col gap-2">
      <div class="flex w-full items-center gap-3">
        <div class="flex min-w-0 flex-1 items-center gap-2">
          <!--
            aria-hidden is what keeps the Material Symbols ligature ("cached", "check_circle")
            out of the announced text: Reka walks the toast's own DOM to build the live region
            and skips hidden nodes.
          -->
          <span
            v-if="showIcon"
            :class="leadingClasses"
            aria-hidden="true"
            data-testid="toast-icon"
          >
            <span
              v-if="loading"
              :class="spinnerClasses"
              data-testid="toast-spinner"
            />
            <ZoneIcon
              v-else
              :icon="resolvedIcon"
              size="lg"
            />
          </span>

          <ToastTitle
            :class="titleClasses"
            data-testid="toast-title"
          >{{ title }}</ToastTitle>

          <!-- Simple shape: Figma runs the link inline, right after the message. -->
          <a
            v-if="detailsHref && !description"
            v-bind="detailsAttrs"
            :class="detailsClasses"
            data-testid="toast-details"
          >{{ detailsLabel }}</a>
        </div>

        <ToastClose
          v-if="dismissible"
          as-child
        >
          <ZoneIconButton
            icon="close"
            size="sm"
            variant="ghost"
            :color="colorSpec.close"
            :aria-label="t('dismiss')"
            data-testid="toast-dismiss"
            @click="$emit('dismiss')"
          />
        </ToastClose>
      </div>

      <ToastDescription
        v-if="description"
        :class="descriptionClasses"
        data-testid="toast-description"
      >{{ description }}</ToastDescription>

      <!-- Detailed shape: Figma right-aligns the link on its own row. -->
      <a
        v-if="detailsHref && description"
        v-bind="detailsAttrs"
        :class="[detailsClasses, 'self-end']"
        data-testid="toast-details"
      >{{ detailsLabel }}</a>
    </div>
  </ToastRoot>
</template>

<script lang="ts">
import { computed, ref, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { ToastClose, ToastDescription, ToastRoot, ToastTitle } from 'reka-ui';
import ZoneIcon, { type IconName } from './Icon.vue';
import type { ButtonColor } from './Button.vue';
import ZoneIconButton from './IconButton.vue';

// INTERNAL to ZoneToastStack: one card of the deck, Reka's ToastRoot with Figma's three shapes
// (simple: title alone; detailed: title + description, boxed at 350px; spinner: `loading`). It
// needs the stack's ToastProvider above it and lands in the stack's viewport. Not for pages: a
// page never renders a toast, the flash does (see the "Flash messages" guide).

export const COLORS = ['neutral-dark', 'neutral-light', 'success', 'warning', 'error'] as const;

export type ToastColor = (typeof COLORS)[number];

export const SENSITIVITIES = ['foreground', 'background'] as const;

export type ToastSensitivity = (typeof SENSITIVITIES)[number];

interface ToastColorSpec {
  /** Surface + border, per the Figma variant set. */
  surface: string;
  title: string;
  description: string;
  /** Text colour on the icon wrapper — ZoneIcon defaults to `current` and inherits it. */
  icon: string;
  link: string;
  /** Track + arc for the spinner shape. */
  spinner: string;
  /** ZoneIconButton ghost colour for the ×. Its shades already match the Figma close glyphs. */
  close: ButtonColor;
  defaultIcon: IconName;
}

// Straight off the Figma variant set (Toast / Toast simple / Toast spinner, node 1966:5349).
// neutral-dark has no border in Figma; it keeps a gray-800 one here purely so every colour has
// the same box model and the shapes line up when they sit side by side.
const COLOR_SPECS: Record<ToastColor, ToastColorSpec> = {
  'neutral-dark': {
    surface: 'bg-gray-800 border-gray-800',
    title: 'text-white',
    description: 'text-gray-300',
    icon: 'text-gray-400',
    link: 'text-white',
    spinner: 'border-gray-600 border-t-primary-500',
    close: 'gray',
    defaultIcon: 'cached',
  },
  'neutral-light': {
    surface: 'bg-white border-gray-100',
    title: 'text-gray-900',
    description: 'text-gray-600',
    icon: 'text-gray-500',
    link: 'text-gray-900',
    spinner: 'border-gray-200 border-t-primary-500',
    close: 'gray',
    defaultIcon: 'cached',
  },
  success: {
    surface: 'bg-green-50 border-green-100',
    title: 'text-green-800',
    description: 'text-green-800',
    icon: 'text-green-800',
    link: 'text-green-800',
    spinner: 'border-green-200 border-t-green-600',
    close: 'green',
    defaultIcon: 'check_circle',
  },
  warning: {
    surface: 'bg-yellow-50 border-yellow-100',
    title: 'text-yellow-800',
    description: 'text-yellow-800',
    icon: 'text-yellow-800',
    link: 'text-yellow-800',
    spinner: 'border-yellow-200 border-t-yellow-600',
    close: 'yellow',
    defaultIcon: 'warning',
  },
  error: {
    surface: 'bg-red-50 border-red-100',
    title: 'text-red-800',
    description: 'text-red-800',
    icon: 'text-red-800',
    link: 'text-red-800',
    spinner: 'border-red-200 border-t-red-600',
    close: 'red',
    defaultIcon: 'cancel',
  },
};

// 2.5rem = the 20px inset on both sides, so a narrow screen gets a card that fits rather than
// one bleeding off the edge. The animations are the Zone theme's, on Reka's data-state (Presence
// waits for the exit one before unmounting): Zone::Toast's entrance, almost opaque from the first
// frame so a card behind never shows through, and the overlay fade out. The transition animates
// the deck classes the stack puts on the card (`class` falls through to this <li>) as it moves.
const TOAST_CLASSES =
  'pointer-events-auto relative flex list-none rounded-lg border p-3 shadow-md ' +
  'max-w-[calc(100vw-2.5rem)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ' +
  'data-[state=open]:animate-toast-in data-[state=closed]:animate-fade-out motion-reduce:animate-none ' +
  'transition-transform duration-300 ease-out motion-reduce:transition-none';
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneToast' });

const { t } = useI18n();

const props = defineProps({
  /**
   * Visibility, not a value. Left unset (null) the card owns its state and shows on mount; pass
   * it and it is controlled, so nothing disappears until the owner writes the prop back. The stack
   * controls its cards. `default: null` rather than `false` keeps Vue from boolean-casting an
   * absent prop.
   */
  modelValue: {
    type: Boolean,
    default: null,
  },
  /** The message. The only thing most toasts need. */
  title: {
    type: String,
    default: '',
  },
  /** A second line. Its presence is what selects Figma's detailed shape over the simple one. */
  description: {
    type: String,
    default: '',
  },
  /** Same names as Zone::Toast's `$toast.show`, so a flash written for the Alpine component maps 1:1. */
  color: {
    type: String as PropType<ToastColor>,
    default: 'neutral-dark',
    validator: (v: string) => COLORS.includes(v as ToastColor),
  },
  /** A ZoneIcon name over the per-colour default (success check_circle, warning warning, error cancel, neutral cached). */
  icon: {
    type: String as PropType<IconName>,
    default: null,
  },
  /** `false` drops the leading icon or spinner. */
  showIcon: {
    type: Boolean,
    default: true,
  },
  /** Figma's spinner shape: the ring replaces the icon. Pair it with `dismissible: false, duration: 0`. */
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * ms before it closes itself; 0 keeps it up until something dismisses it. Hovering the viewport
   * pauses the countdown and resumes on leave.
   */
  duration: {
    type: Number,
    default: 4000,
  },
  /** Gates the × button and Escape. `false` leaves only the timeout / the owner. */
  dismissible: {
    type: Boolean,
    default: true,
  },
  /** Text of the "See details" link. */
  detailsText: {
    type: String,
    default: null,
  },
  /** Its presence is what renders the "See details" link. */
  detailsHref: {
    type: String,
    default: null,
  },
  /** `null` opens in the same tab (and drops the rel that only `_blank` needs). */
  detailsTarget: {
    type: String,
    default: '_blank',
  },
  /**
   * Live-region politeness: `foreground` is assertive, for something the user just did;
   * `background` is polite, for anything they did not just trigger (a poll finished, a job
   * completed elsewhere).
   */
  sensitivity: {
    type: String as PropType<ToastSensitivity>,
    default: 'foreground',
    validator: (v: string) => SENSITIVITIES.includes(v as ToastSensitivity),
  },
});

const emit = defineEmits<{
  /** It closed, for the v-model form. */
  'update:modelValue': [visible: boolean];
  /** Closed for any reason: timeout, ×, swipe, Escape. */
  close: [];
  /** The × was clicked specifically (a subset of `close`). */
  dismiss: [];
  /** The hover pause of the countdown started. */
  pause: [];
  /** The countdown resumed. */
  resume: [];
}>();

const uncontrolledOpen = ref(true);

const detailsLabel = computed<string>(() => props.detailsText ?? t('details'));

const isControlled = computed<boolean>(() => props.modelValue !== null);

const isOpen = computed<boolean>(() => (isControlled.value ? props.modelValue : uncontrolledOpen.value));

const colorSpec = computed<ToastColorSpec>(() => COLOR_SPECS[props.color]);

const resolvedIcon = computed<IconName>(() => props.icon || colorSpec.value.defaultIcon);

const toastClasses = computed<string>(() => [
  TOAST_CLASSES,
  colorSpec.value.surface,
  // Figma boxes the detailed shape at 350px so the description has a stable measure; the
  // simple one hugs its content.
  props.description ? 'w-[350px]' : '',
]
  .filter(Boolean)
  .join(' '));

const leadingClasses = computed<string>(() => `flex shrink-0 items-center ${colorSpec.value.icon}`);

const spinnerClasses = computed<string>(() => `block size-6 animate-spin rounded-full border-[3px] ${colorSpec.value.spinner}`);

const titleClasses = computed<string>(() => {
  // Figma: 16px semibold once there is a description under it, 14px regular on its own.
  const shape = props.description ? 'flex-1 text-md font-semibold' : 'text-sm';

  return `min-w-0 ${shape} ${colorSpec.value.title}`;
});

const descriptionClasses = computed<string>(() => `w-full text-sm ${colorSpec.value.description}`);

const detailsClasses = computed<string>(() => `shrink-0 text-sm font-medium underline ${colorSpec.value.link}`);

const detailsAttrs = computed<Record<string, unknown>>(() => ({
  href: props.detailsHref,
  target: props.detailsTarget || undefined,
  // Only `_blank` needs the opener guard; a same-tab link should not carry it.
  rel: props.detailsTarget === '_blank' ? 'noopener noreferrer' : undefined,
}));

function onOpenChange(value: boolean) {
  if (value === isOpen.value) {
    return;
  }

  if (!isControlled.value) {
    uncontrolledOpen.value = value;
  }

  emit('update:modelValue', value);

  if (!value) {
    emit('close');
  }
}

/**
 * Reka closes on Escape unless the event it emitted was default-prevented, so this is how
 * `dismissible: false` swallows the key without also disabling the timeout.
 */
function onEscapeKeyDown(event: KeyboardEvent) {
  if (!props.dismissible) {
    event.preventDefault();
  }
}
</script>

<i18n lang="json">
{
  "en": {
    "details": "See details",
    "dismiss": "Dismiss"
  }
}
</i18n>
